import { useEffect, useMemo, useState } from "react";
import type {
  AdminSession,
  BillingCancellationEligibleDocument,
  BillingCancellationHistoryItem,
  BillingCancellationPreview,
} from "@/lib/api";
import {
  createBillingCancellationGlobal,
  createBillingCancellationNormal,
  getBillingCancellationFileUrl,
  getBillingCancellationPreview,
  listBillingCancellations,
  listBillingCancellationEligibleDocuments,
} from "@/lib/api";

type Props = {
  open: boolean;
  onClose: () => void;
  onCompleted: () => Promise<void> | void;
  session: AdminSession | null;
};

type ModalTab = "normal" | "global";
type HistoryStatusFilter = "without_failed" | "all" | "success" | "processing" | "failed";

function todayLocal(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatMoney(value: number): string {
  return `$${value.toLocaleString("es-CL")}`;
}

function openFile(url: string): void {
  window.open(url, "_blank", "noopener,noreferrer");
}

export default function BillingCancellationModal({ open, onClose, onCompleted, session }: Props) {
  const [tab, setTab] = useState<ModalTab>("normal");
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<BillingCancellationEligibleDocument[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<BillingCancellationEligibleDocument | null>(null);
  const [preview, setPreview] = useState<BillingCancellationPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDate, setHistoryDate] = useState(todayLocal());
  const [historyStatusFilter, setHistoryStatusFilter] = useState<HistoryStatusFilter>("without_failed");
  const [history, setHistory] = useState<BillingCancellationHistoryItem[]>([]);
  const [detailNormal, setDetailNormal] = useState("ANULA DOC DE REFERENCIA");
  const [normalDate, setNormalDate] = useState(todayLocal());
  const [globalDate, setGlobalDate] = useState(todayLocal());
  const [globalType, setGlobalType] = useState<"39" | "33" | "34" | "41" | "48">("39");
  const [globalReferenceReason, setGlobalReferenceReason] = useState("ANULA DOC DE REFERENCIA");
  const [globalDetail, setGlobalDetail] = useState("ANULA DOC DE REFERENCIA");
  const [globalAmount, setGlobalAmount] = useState("");
  const [lastFile, setLastFile] = useState<{ url: string; path: string; filename: string; cancellationId?: string } | null>(null);

  const selectedPaymentsTotal = useMemo(
    () => preview?.payments.reduce((acc, item) => acc + item.allocatedAmount, 0) ?? 0,
    [preview],
  );
  const filteredHistory = useMemo(() => {
    if (historyStatusFilter === "all") return history;
    if (historyStatusFilter === "without_failed") {
      return history.filter((item) => item.status !== "failed");
    }
    return history.filter((item) => item.status === historyStatusFilter);
  }, [history, historyStatusFilter]);

  async function loadHistory(date = historyDate): Promise<void> {
    if (!session) return;
    setHistoryLoading(true);
    try {
      const rows = await listBillingCancellations(session, { date, limit: 25 });
      setHistory(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo cargar historial");
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  useEffect(() => {
    if (!open || !session) return;
    void loadHistory(historyDate);
  }, [open, session, historyDate]);

  useEffect(() => {
    if (!open) return;
    setError("");
  }, [open, tab]);

  if (!open) return null;

  async function handleSearch(): Promise<void> {
    if (!session) return;
    setSearching(true);
    setError("");
    try {
      const rows = await listBillingCancellationEligibleDocuments(session, { q: query });
      setResults(rows);
      if (rows.length === 0) {
        setError("No se encontraron documentos para revisar.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo buscar documentos");
      setResults([]);
    } finally {
      setSearching(false);
    }
  }

  async function handleSelectDocument(row: BillingCancellationEligibleDocument): Promise<void> {
    if (!session) return;
    setSelectedDocument(row);
    setPreview(null);
    setError("");
    setLoadingPreview(true);
    try {
      const response = await getBillingCancellationPreview(session, row.id, detailNormal);
      setPreview(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo cargar la vista previa");
    } finally {
      setLoadingPreview(false);
    }
  }

  async function handleCreateNormal(): Promise<void> {
    if (!session || !selectedDocument || !preview) return;
    const detail = detailNormal.trim();
    if (!detail) {
      setError("Debes ingresar un detalle de anulacion.");
      return;
    }
    if (!preview.canCancel) {
      setError(preview.blockingReason ?? "El documento no puede anularse.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const response = await createBillingCancellationNormal(session, {
        billingDocumentId: selectedDocument.id,
        referenceReasonCode: "1",
        detail,
        cancellationDate: normalDate,
      });
      setLastFile({ ...response.file, cancellationId: response.cancellation.id });
      setSelectedDocument(null);
      setPreview(null);
      setResults([]);
      setQuery("");
      await loadHistory(historyDate);
      await onCompleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo generar la nota de credito");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCreateGlobal(): Promise<void> {
    if (!session) return;
    const detail = globalDetail.trim();
    const amount = Number(globalAmount);
    if (!detail) {
      setError("Debes ingresar el detalle de la NC global.");
      return;
    }
    const referenceReasonText = globalReferenceReason.trim();
    if (!referenceReasonText) {
      setError("Debes ingresar la razon de referencia.");
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Debes ingresar un monto valido.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const response = await createBillingCancellationGlobal(session, {
        originalDocumentType: globalType,
        cancellationDate: globalDate,
        referenceReasonCode: "1",
        referenceReasonText,
        detail,
        amount,
      });
      setLastFile({ ...response.file, cancellationId: response.cancellation.id });
      setGlobalAmount("");
      await loadHistory(historyDate);
      await onCompleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo generar la NC global");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleOpenHistoryFile(item: BillingCancellationHistoryItem, kind: "pdf" | "xml"): Promise<void> {
    if (!session) return;
    try {
      const file = await getBillingCancellationFileUrl(session, item.id, kind);
      openFile(file.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : `No se pudo abrir ${kind.toUpperCase()}`);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/45 p-4">
      <div className="mx-auto flex h-full w-full max-w-7xl items-center justify-center">
        <div className="flex max-h-[94vh] w-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <div>
              <h2 className="text-lg font-bold text-slate-800">Anulación de documentos</h2>
              <p className="text-sm text-slate-500">Genera notas de crédito y revisa el historial de anulaciones.</p>
            </div>
            <button
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[1.4fr_0.9fr]">
            <div className="min-h-0 overflow-y-auto border-r border-slate-100 px-6 py-5">
              <div className="mb-5 flex items-center gap-2 rounded-2xl bg-slate-100 p-1">
                <button
                  onClick={() => setTab("normal")}
                  className={`rounded-xl px-4 py-2 text-sm font-medium transition ${tab === "normal" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500"}`}
                >
                  NC normal
                </button>
                <button
                  onClick={() => setTab("global")}
                  className={`rounded-xl px-4 py-2 text-sm font-medium transition ${tab === "global" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500"}`}
                >
                  NC global
                </button>
              </div>

              {error ? (
                <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
              ) : null}

              {lastFile ? (
                <div className="mb-5 rounded-3xl border border-emerald-200 bg-emerald-50 p-5">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-emerald-800">Nota de crédito generada</h3>
                      <p className="mt-1 text-sm text-emerald-700">{lastFile.filename}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => openFile(lastFile.url)}
                        className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100"
                      >
                        Ver PDF
                      </button>
                      <button
                        onClick={() => setLastFile(null)}
                        className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                      >
                        Cerrar
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {tab === "normal" ? (
                <div className="space-y-5">
                  <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-3 lg:flex-row">
                      <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void handleSearch();
                        }}
                        placeholder="Buscar por contrato, cliente o folio"
                        className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm"
                      />
                      <button
                        onClick={() => void handleSearch()}
                        disabled={searching}
                        className="rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {searching ? "Buscando..." : "Buscar"}
                      </button>
                    </div>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-[0.95fr_1.05fr]">
                    <div className="rounded-3xl border border-slate-200 bg-white">
                      <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-700">Documentos</div>
                      <div className="max-h-[22rem] overflow-y-auto">
                        {results.length === 0 ? (
                          <div className="px-4 py-6 text-sm text-slate-500">Sin resultados.</div>
                        ) : (
                          <div className="divide-y divide-slate-100">
                            {results.map((row) => (
                              <button
                                key={row.id}
                                onClick={() => void handleSelectDocument(row)}
                                className={`w-full px-4 py-3 text-left transition hover:bg-slate-50 ${selectedDocument?.id === row.id ? "bg-indigo-50" : ""}`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <div className="text-sm font-semibold text-slate-800">
                                      {row.contractNumber} - {row.customerName}
                                    </div>
                                    <div className="mt-1 text-xs text-slate-500">
                                      DTE {row.documentType} folio {row.folio || "-"} - {formatMoney(row.totalAmount)}
                                    </div>
                                  </div>
                                  <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${row.canCancel ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                                    {row.canCancel ? "Disponible" : "Bloqueado"}
                                  </span>
                                </div>
                                {!row.canCancel && row.blockingReason ? (
                                  <div className="mt-2 text-xs text-amber-700">{row.blockingReason}</div>
                                ) : null}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-slate-200 bg-white p-4">
                      {loadingPreview ? (
                        <div className="flex h-56 items-center justify-center text-sm text-slate-500">Cargando vista previa...</div>
                      ) : !preview ? (
                        <div className="flex h-56 items-center justify-center text-sm text-slate-500">Selecciona un documento para revisar la anulación.</div>
                      ) : (
                        <div className="space-y-4">
                          <div className="grid gap-3 md:grid-cols-2">
                            <div className="rounded-2xl bg-slate-50 p-3">
                              <div className="text-xs uppercase tracking-wide text-slate-500">Documento original</div>
                              <div className="mt-1 text-sm font-semibold text-slate-800">
                                DTE {preview.originalDocument.documentType} folio {preview.originalDocument.folio || "-"}
                              </div>
                              <div className="mt-1 text-sm text-slate-600">{preview.originalDocument.customerName}</div>
                              <div className="text-xs text-slate-500">{preview.originalDocument.contractNumber}</div>
                            </div>
                            <div className="rounded-2xl bg-slate-50 p-3">
                              <div className="text-xs uppercase tracking-wide text-slate-500">Totales</div>
                              <div className="mt-1 text-sm text-slate-700">Neto: {formatMoney(preview.originalDocument.netAmount)}</div>
                              <div className="text-sm text-slate-700">IVA: {formatMoney(preview.originalDocument.taxAmount)}</div>
                              <div className="text-sm font-semibold text-slate-800">Total: {formatMoney(preview.originalDocument.totalAmount)}</div>
                            </div>
                          </div>

                          {preview.payments.length > 0 ? (
                            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                              El documento tiene pagos asociados por {formatMoney(selectedPaymentsTotal)}. Debes anular el pago antes de generar la NC.
                            </div>
                          ) : null}

                          <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700">Fecha de anulación</label>
                            <input
                              type="date"
                              value={normalDate}
                              onChange={(e) => setNormalDate(e.target.value)}
                              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                            />
                          </div>
                          <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700">Detalle</label>
                            <textarea
                              value={detailNormal}
                              onChange={(e) => setDetailNormal(e.target.value)}
                              rows={3}
                              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                            />
                          </div>

                          <div className="rounded-2xl border border-slate-200">
                            <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-700">Ítems facturables</div>
                            <div className="divide-y divide-slate-100">
                              {preview.items.map((item) => (
                                <div key={item.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                                  <div className="text-slate-700">{item.description}</div>
                                  <div className="font-medium text-slate-900">{formatMoney(item.totalPrice)}</div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="flex justify-end">
                            <button
                              onClick={() => void handleCreateNormal()}
                              disabled={submitting || !preview.canCancel}
                              className="rounded-2xl bg-rose-600 px-5 py-3 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-50"
                            >
                              {submitting ? "Generando..." : "Generar nota de crédito"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">Tipo documento origen</label>
                      <select
                        value={globalType}
                        onChange={(e) => setGlobalType(e.target.value as "39" | "33" | "34" | "41" | "48")}
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                      >
                        <option value="39">39 - Boleta afecta</option>
                        <option value="33">33 - Factura afecta</option>
                        <option value="41">41 - Boleta exenta</option>
                        <option value="34">34 - Factura exenta</option>
                        <option value="48">48 - Comprobante electrónico</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">Fecha de anulación</label>
                      <input
                        type="date"
                        value={globalDate}
                        onChange={(e) => setGlobalDate(e.target.value)}
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Razon referencia</label>
                    <input
                      value={globalReferenceReason}
                      onChange={(e) => setGlobalReferenceReason(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Detalle item (PDF)</label>
                    <textarea
                      value={globalDetail}
                      onChange={(e) => setGlobalDetail(e.target.value)}
                      rows={4}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Monto</label>
                    <input
                      value={globalAmount}
                      onChange={(e) => setGlobalAmount(e.target.value)}
                      placeholder="12000"
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                    />
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                    Receptor global fijo: <strong>DTE global.</strong> RUT genérico. Esta NC no altera `BillingDocument` ni `ContractBalance`.
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={() => void handleCreateGlobal()}
                      disabled={submitting}
                      className="rounded-2xl bg-rose-600 px-5 py-3 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-50"
                    >
                      {submitting ? "Generando..." : "Generar NC global"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="min-h-0 overflow-y-auto bg-slate-50 px-6 py-5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Historial de NC</h3>
                  <p className="text-xs text-slate-500">Trazabilidad de anulaciones emitidas.</p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={historyStatusFilter}
                    onChange={(e) => setHistoryStatusFilter(e.target.value as HistoryStatusFilter)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    <option value="without_failed">Sin fallidas</option>
                    <option value="all">Todos</option>
                    <option value="success">Exitosas</option>
                    <option value="processing">En proceso</option>
                    <option value="failed">Fallidas</option>
                  </select>
                  <input
                    type="date"
                    value={historyDate}
                    onChange={(e) => setHistoryDate(e.target.value)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                  />
                </div>
              </div>

              {historyLoading ? (
                <div className="flex h-40 items-center justify-center text-sm text-slate-500">Cargando historial...</div>
              ) : filteredHistory.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-6 text-sm text-slate-500">
                  No hay notas de crédito para la fecha y estado seleccionados.
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredHistory.map((item) => (
                    <div key={item.id} className="rounded-3xl border border-slate-200 bg-white p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-slate-800">
                            NC {item.creditNoteFolio || "-"} - {item.mode === "normal" ? "Normal" : "Global"}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            Ref {item.originalDocumentType}/{item.originalFolio || "0"} - {item.contractNumber || "Sin contrato"}
                          </div>
                        </div>
                        <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${item.status === "success" ? "bg-emerald-100 text-emerald-700" : item.status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                          {item.status}
                        </span>
                      </div>
                      <div className="mt-3 text-sm text-slate-700">{item.detail}</div>
                      <div className="mt-2 text-xs text-slate-500">
                        {item.customerName || "Sin receptor APR"} - {formatMoney(item.amount)}
                      </div>
                      {item.lastError ? (
                        <div className="mt-2 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{item.lastError}</div>
                      ) : null}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          onClick={() => void handleOpenHistoryFile(item, "pdf")}
                          className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-200"
                        >
                          Ver PDF
                        </button>
                        <button
                          onClick={() => void handleOpenHistoryFile(item, "xml")}
                          className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-200"
                        >
                          Ver XML
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
